import { useEffect, useState } from 'react';
import { casinos } from '../../../mocks/casinos';
import { openPlayLinkByBrand } from '../../../config/playLinks';

const TopCasinos = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [showAll, setShowAll] = useState(false);
  const filters = ['All', 'New', 'Best Bonus', 'Fast Payout'];
  const defaultVisibleCount = 5;
  const filterCasinoNames: Record<string, string[]> = {
    New: ['ipay9vip', 'mrboom9'],
    'Best Bonus': ['bigpay77', 'me99', 'gucci9', 'spinaroo', 'mrbean9', 'queen13'],
    'Fast Payout': ['ipay9', 'kingbet9', 'rolex9', 'micky9', 'winnie777', 'bybid9']
  };

  const getCasinoKey = (name: string) =>
    name.toLowerCase().replace(' online casino', '').replace(/\s+/g, '');

  const filteredCasinos =
    activeFilter === 'All'
      ? casinos
      : casinos
          .filter((casino) => filterCasinoNames[activeFilter]?.includes(getCasinoKey(casino.name)))
          .sort((a, b) => {
            const selected = filterCasinoNames[activeFilter] ?? [];
            return selected.indexOf(getCasinoKey(a.name)) - selected.indexOf(getCasinoKey(b.name));
          });

  useEffect(() => {
    setShowAll(false);
  }, [activeFilter]);

  const visibleCasinos = showAll
    ? filteredCasinos
    : filteredCasinos.slice(0, defaultVisibleCount);

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <i key={`full-${i}`} className="ri-star-fill text-yellow-400"></i>
      );
    }
    if (hasHalfStar) {
      stars.push(
        <i key="half" className="ri-star-half-fill text-yellow-400"></i>
      );
    }
    const remaining = 5 - Math.ceil(rating);
    for (let i = 0; i < remaining; i++) {
      stars.push(
        <i key={`empty-${i}`} className="ri-star-line text-gray-300"></i>
      );
    }
    return stars;
  };

  const handleVisitCasino = (casinoName: string) => {
    openPlayLinkByBrand(getCasinoKey(casinoName));
  };

  return (
    <section id="top-casinos" className="py-12 sm:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Top Rank Australia Online Ewallet Casinos
          </h2>
          <p className="text-gray-600 text-md">
            Our expert team has reviewed and ranked the best online casinos based on bonuses, game selection, security, and payout speed.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="mb-8 border-b border-gray-200 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex min-w-max gap-2 sm:gap-3">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-4 sm:px-5 py-3 text-sm font-medium transition-all whitespace-nowrap ${
                  activeFilter === filter
                    ? 'text-green-600 border-b-2 border-green-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        {/* Casino List */}
        <div className="space-y-4">
          {visibleCasinos.map((casino) => (
            <div
              key={casino.rank}
              className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
                {/* Rank Badge */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xl">
                      #{casino.rank}
                    </span>
                  </div>
                </div>

                {/* Casino Logo */}
                <div className="flex-shrink-0">
                  <div className="w-36 h-24 bg-white rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden">
                    <img
                      src={casino.logo}
                      alt={casino.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Casino Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                        {casino.name}
                      </h3>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex gap-0.5">
                          {renderStars(casino.rating)}
                        </div>
                        <span className="text-sm font-semibold text-gray-900">
                          {casino.rating}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-3">
                    {casino.description}
                  </p>

                  {/* Bonus Highlight */}
                  <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2 mb-3">
                    <i className="ri-gift-line text-green-600"></i>
                    <span className="text-green-700 font-semibold text-sm">
                      {casino.bonus}
                    </span>
                  </div>

                  {/* Casino Stats */}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-gamepad-line text-gray-400"></i>
                      <span>{casino.games} Games</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-money-dollar-circle-line text-gray-400"></i>
                      <span>Min Deposit: {casino.minDeposit}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-time-line text-gray-400"></i>
                      <span>Payout: {casino.payoutSpeed}</span>
                    </div>
                  </div>

                  {/* T&Cs Apply */}
                  <p className="text-xs text-gray-500">
                    T&Cs Apply. 18+ Only. KoalaBet.win
                  </p>
                </div>

                {/* CTA Buttons */}
                <div className="w-full sm:w-auto flex sm:flex-col gap-2 sm:items-stretch">
                  <button
                    type="button"
                    onClick={() => handleVisitCasino(casino.name)}
                    className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 min-h-[44px] rounded-lg transition-colors whitespace-nowrap"
                  >
                    Visit Casino
                  </button>
                  <button className="flex-1 sm:flex-none text-green-600 hover:text-green-700 font-medium text-sm min-h-[44px] whitespace-nowrap">
                    Read Review →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More */}
        {!showAll && filteredCasinos.length > defaultVisibleCount && (
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAll(true)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold px-8 py-3 rounded-lg transition-colors whitespace-nowrap"
            >
              Load More Casinos
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default TopCasinos;